import { Resource } from '../../../Resource/Types';

export interface TypeaheadPayload<T> {
  query: string;
  suggestions: T[];
}

export type TypeaheadResource<T> = Resource<TypeaheadPayload<T>>;

export interface Idle {
  kind: 'idle';
}

export interface Loading {
  kind: 'loading';
  query: string;
}

export interface Loaded<T> {
  kind: 'loaded';
  query: string;
  suggestions: ReadonlyArray<T>;
}

export interface TypeaheadError {
  kind: 'error';
  query: string;
  message: string;
}

export type TypeaheadState<T> = Idle | Loading | Loaded<T> | TypeaheadError;

export const idle = (): Idle => ({ kind: 'idle' });

export const loading = (query: string): Loading => ({
  kind: 'loading',
  query,
});

export const loaded = <T>(query: string, suggestions: ReadonlyArray<T>): Loaded<T> => ({
  kind: 'loaded',
  query,
  suggestions,
});

export const error = (query: string, message: string): TypeaheadError => ({
  kind: 'error',
  query,
  message,
});

export const getCurrentSuggestions = <T>(state: TypeaheadState<T>): ReadonlyArray<T> => {
  switch (state.kind) {
    case 'loaded':
      return state.suggestions;
    case 'idle':
    case 'loading':
    case 'error':
      return [];
  }
};

export const isLoading = <T>(state: TypeaheadState<T>): boolean => {
  return state.kind === 'loading';
};

// Selection types for handling both existing items and new item creation
export interface ExistingItemSelection<T> {
  kind: 'existing';
  item: T;
}

export interface NewItemSelection {
  kind: 'new';
  name: string;
}

export type TypeaheadSelection<T> = ExistingItemSelection<T> | NewItemSelection;

export const createExistingSelection = <T>(item: T): ExistingItemSelection<T> => ({
  kind: 'existing',
  item,
});

export const createNewSelection = (name: string): NewItemSelection => ({
  kind: 'new',
  name,
});
